import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, Input } from '@angular/core';
import { MessageService, SortEvent } from 'primeng/api';
import { Router } from '@angular/router';
import { GrupoDTO, GrupoFormDTO } from '../../../models/grupo.model';
import { GrupoService } from '../../../services/grupo/grupo.service';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { slugificarCategoria } from '../../cardapio-digital/utils/cardapio-slug.util';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TableColumn } from '../../../models/shared/table-column.model';
import { FileUpload } from 'primeng/fileupload';
import { environment } from '../../../environments/environment';
import { ImagemUtils } from '../../../shared/utils/imagem-utils';

@Component({
  selector: 'app-grupo',
  templateUrl: './grupo.component.html',
  styleUrls: ['./grupo.component.scss'],
  providers: [MessageService],
})
export class GrupoComponent implements OnInit, OnDestroy {
  /**
   * Modo EMBUTIDO (Operar Cliente / Área do Contador): opera sob o CONTEXTO do
   * cliente (X-Empresa-Contexto). Esconde o page-header (o Operar Cliente já tem
   * o seu) e oculta ações fora do escopo add/edit sob contexto: excluir (o
   * backend não libera DELETE /grupo no contexto) e compartilhar no Mini Shop
   * (usaria o slug do CONTADOR, não o do cliente).
   */
  @Input() embutido = false;

  /** Id da empresa do CLIENTE (só no modo embutido). Usado nas URLs de imagem. */
  @Input() empresaContextoId?: number | null;

  env = environment;
  idEmpresa: string = '';

  // Slug da loja no Mini Shop (urlDoMiniShopping da empresa logada). Usado para
  // montar a URL publica da categoria (/menu/{slugLoja}?categoria=...) na acao
  // de compartilhar. Carregado em carregarConfiguracaoEmpresa(). Mesmo padrao
  // adotado na listagem de produtos (ListaDeProdutosComponent).
  slugLojaMiniShop: string = '';

  grupos: GrupoDTO[] = [];
  gruposOriginal: GrupoDTO[] = [];
  first: number = 0;
  rows: number = 10;
  private _lastRows: number = 10;
  totalRecords = 0;
  loading: boolean = true;
  searchNome: string = '';
  lastfield: string = 'id';
  lastSort: string = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  form: GrupoFormDTO = this.createEmptyForm();
  isFormSubmitted = false;
  submitting = false;
  activeTabIndex: number = 1;

  // ---- Upload / preview
  imageBase64: string | null = null;
  imagePreviewUrl: string | null = null;
  fileName: string | null = null;
  imagemRemovida: boolean = false;

  // ViewChild para o componente de upload de imagem
  @ViewChild('fileUploader') fileUploaderRef!: FileUpload;

  // ViewChild para foco no campo de busca (F2)
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  // ViewChild para foco no botao de confirmar exclusao
  @ViewChild('deleteConfirmBtn') deleteConfirmBtnRef!: ElementRef<HTMLButtonElement>;

  // Dialog de exclusão
  deleteDialogVisible = false;
  deleteGrupoId: string | null = null;

  // Dialog de imagem ampliada
  imagemAmpliadaVisible = false;
  imagemAmpliadaSrc: string | null = null;
  imagemAmpliadaNome: string = '';

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'grupo-visible-columns';

  columns: TableColumn[] = [
    { key: 'imagem', labelKey: 'grupo.tabela.imagem', visible: true, fixed: false, defaultVisible: true },
    { key: 'codigoGrupo', labelKey: 'grupo.tabela.codigo_grupo', visible: true, fixed: true, defaultVisible: true },
    { key: 'nome', labelKey: 'grupo.tabela.nome', visible: true, fixed: true, defaultVisible: true },
    { key: 'status', labelKey: 'grupo.tabela.status', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'grupo.tabela.data_da_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'grupo.tabela.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  // Textos traduzidos
  texts = {
    titulo: '',
    subtitulo: '',
    dados_do_grupo: '',
    tabelaDeGrupos: '',
    nenhumGrupo: '',
    adicioneNovoGrupo: '',
    confirmacao: '',
    confirmarExclusao: '',
    carregando: '',
    ajuste_filtros: '',
    aba_cadastro: '',
    aba_lista: '',
  };

  labels = {
    nome: '',
    status: '',
    imagem: '',
    itens_por_pagina: '',
  };

  placeholders = {
    nome: '',
    pesquisar: '',
  };

  botoes = {
    adicionar: '',
    selecionarImagem: '',
    remover: '',
    salvar: '',
    limpar: '',
    ativo: '',
    inativo: '',
    novo_grupo: '',
    confirmar: '',
    cancelar: '',
  };

  tabela = {
    codigoGrupo: '',
    imagem: '',
    nome: '',
    status: '',
    dataInclusao: '',
    acoes: '',
  };

  tooltips = {
    editar: '',
    excluir: '',
    compartilhar: '',
    compartilhar_indisponivel: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private messageService: MessageService,
    private grupoService: GrupoService,
    private router: Router,
    private translate: TranslateService,
    private empresaConfigService: EmpresaConfigService
  ) {}

  ngOnInit() {
    // No modo embutido a empresa é a do CLIENTE (contexto), não a do contador (sessão).
    this.idEmpresa = this.embutido
      ? String(this.empresaContextoId ?? '')
      : (sessionStorage.getItem('id') || '');
    this.loadTranslations();
    this.loadColumnPreferences();
    // Slug do Mini Shop é da empresa da sessão (contador) — não faz sentido no
    // contexto do cliente, e a ação de compartilhar fica oculta no embutido.
    if (!this.embutido) {
      this.carregarConfiguracaoEmpresa();
    }
    this.findAtividade();

    // Atualiza labels quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.loadTranslations();
    });
  }

  ngOnDestroy() {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.titulo = this.translate.instant('grupo.texts.titulo_criar');
      this.texts.subtitulo = this.translate.instant('grupo.texts.subtitulo_lista');
    } else {
      const isEdicao = !!this.form.id;
      this.texts.titulo = this.translate.instant(
        isEdicao ? 'grupo.texts.titulo_editar' : 'grupo.texts.titulo_criar'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'grupo.texts.subtitulo_editar' : 'grupo.texts.subtitulo_criar'
      );
    }
  }

  private loadTranslations() {
    const isEdicao = !!this.form.id;
    const isListaAtiva = this.activeTabIndex === 1;
    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva ? 'grupo.texts.titulo_criar' : isEdicao ? 'grupo.texts.titulo_editar' : 'grupo.texts.titulo_criar'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'grupo.texts.subtitulo_lista'
          : isEdicao ? 'grupo.texts.subtitulo_editar' : 'grupo.texts.subtitulo_criar'
      ),
      dados_do_grupo: this.translate.instant('grupo.texts.dados_do_grupo'),
      tabelaDeGrupos: this.translate.instant('grupo.texts.tabela_de_grupos'),
      nenhumGrupo: this.translate.instant('grupo.texts.nenhum_grupo'),
      adicioneNovoGrupo: this.translate.instant('grupo.texts.adicione_novo_grupo'),
      confirmacao: this.translate.instant('grupo.texts.confirmacao'),
      confirmarExclusao: this.translate.instant('grupo.texts.confirmar_exclusao'),
      carregando: this.translate.instant('grupo.texts.carregando'),
      ajuste_filtros: this.translate.instant('grupo.texts.ajuste_filtros'),
      aba_cadastro: this.translate.instant('grupo.texts.aba_cadastro'),
      aba_lista: this.translate.instant('grupo.texts.aba_lista'),
    };

    this.labels = {
      nome: this.translate.instant('grupo.labels.nome'),
      status: this.translate.instant('grupo.labels.status'),
      imagem: this.translate.instant('grupo.labels.imagem'),
      itens_por_pagina: this.translate.instant('grupo.labels.itens_por_pagina'),
    };

    this.placeholders = {
      nome: this.translate.instant('grupo.placeholders.nome_do_grupo'),
      pesquisar: this.translate.instant('grupo.placeholders.pesquisar'),
    };

    this.botoes = {
      adicionar: this.translate.instant('grupo.botoes.adicionar'),
      selecionarImagem: this.translate.instant('grupo.botoes.selecionar_imagem'),
      remover: this.translate.instant('grupo.botoes.remover'),
      salvar: this.translate.instant('grupo.botoes.salvar'),
      limpar: this.translate.instant('grupo.botoes.limpar'),
      ativo: this.translate.instant('grupo.botoes.ativo'),
      inativo: this.translate.instant('grupo.botoes.inativo'),
      novo_grupo: this.translate.instant('grupo.botoes.novo_grupo'),
      confirmar: this.translate.instant('grupo.botoes.confirmar'),
      cancelar: this.translate.instant('grupo.botoes.cancelar'),
    };

    this.tabela = {
      codigoGrupo: this.translate.instant('grupo.tabela.codigo_grupo'),
      imagem: this.translate.instant('grupo.tabela.imagem'),
      nome: this.translate.instant('grupo.tabela.nome'),
      status: this.translate.instant('grupo.tabela.status'),
      dataInclusao: this.translate.instant('grupo.tabela.data_da_inclusao'),
      acoes: this.translate.instant('grupo.tabela.acoes'),
    };

    this.tooltips = {
      editar: this.translate.instant('grupo.tooltips.editar'),
      excluir: this.translate.instant('grupo.tooltips.excluir'),
      compartilhar: this.translate.instant('grupo.tooltips.compartilhar'),
      compartilhar_indisponivel: this.translate.instant('grupo.tooltips.compartilhar_indisponivel'),
    };
  }

  /**
   * Carrega o slug da loja no Mini Shop (urlDoMiniShopping da empresa logada),
   * usado para montar a URL publica da categoria na acao de compartilhar.
   * Usa EmpresaConfigService.getSessao() — mesmo padrao da listagem de produtos.
   * Pode vir vazio se a loja ainda nao configurou o Mini Shop.
   */
  carregarConfiguracaoEmpresa(): void {
    this.empresaConfigService.getSessao().subscribe({
      next: (res) => {
        this.slugLojaMiniShop = (res?.urlDoMiniShopping || '').trim();
      },
      error: () => {
        this.slugLojaMiniShop = '';
      },
    });
  }

  findAtividade(
    page: number = 0,
    size: number = this.rows,
    sort: string = 'id,asc'
  ) {
    this.loading = true;
    this.grupoService
      .get('/grupo', { page, size, sort })
      .subscribe({
        next: (response) => {
          this.gruposOriginal = response.content || [];
          this.grupos = [...this.gruposOriginal];
          this.totalRecords = response.totalElements;
          this.first = page * size;
          this.loading = false;

          // Reaplicar filtro local se houver termo de busca ativo
          if (this.searchNome && this.searchNome.trim().length > 0) {
            this.filtrarLocal(this.searchNome.trim());
          }
        },
        error: () => {
          this.gruposOriginal = [];
          this.grupos = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
  }

  onPageChange(event: any) {
    const page = event.page;
    const size = event.rows;
    this.findAtividade(page, size, `${this.lastfield},${this.lastSort}`);
  }

  onRowsPerPageChange() {
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
    this.findAtividade(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  customSort(event: SortEvent) {
    if (!event.field || !event.data) return;

    const field = event.field;
    const order = event.order ?? 1;

    this.lastfield = field;
    this.lastSort = order === 1 ? 'asc' : 'desc';

    event.data.sort((a: any, b: any) => {
      const valueA = this.getSortValue(a, field);
      const valueB = this.getSortValue(b, field);

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return -1 * order;
      if (valueB == null) return 1 * order;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return valueA.localeCompare(valueB) * order;
      }

      return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * order;
    });
  }

  private getSortValue(item: any, field: string): any {
    const parts = field.split('.');
    let value: any = item;
    for (const part of parts) {
      if (value == null) return null;
      value = value[part];
    }
    if (value != null && typeof value === 'object' && 'nome' in value) {
      return value.nome;
    }
    return value;
  }

  // ========= FILE UPLOAD =========
  async onSelectImage(event: any) {
    const file: File | undefined = event.files?.[0];
    if (!file) { return; }

    if (!file.type.startsWith('image/')) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('grupo.texts.erro'),
        detail: this.translate.instant('grupo.texts.arquivo_invalido'),
      });
      return;
    }

    this.fileName = file.name;
    this.imagemRemovida = false;

    const imagem = await ImagemUtils.comprimirParaBase64(file);
    this.imagePreviewUrl = imagem.dataUrl;
    this.imageBase64 = imagem.base64;
  }

  onClearImage() {
    // Se está editando e tinha imagem, marca como removida
    if (this.form.id && (this.imagePreviewUrl || this.form.imagem)) {
      this.imagemRemovida = true;
    }
    this.fileName = null;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;

    // Limpa o estado interno do p-fileUpload (arquivos selecionados e preview)
    if (this.fileUploaderRef) {
      this.fileUploaderRef.clear();
    }
  }

  // =================================

  onSearchChange(value: string): void {
    this.searchNome = value || '';
    if (this.searchNome.trim().length > 0) {
      this.first = 0;
      this.filtrarLocal(this.searchNome.trim());
    } else {
      this.grupos = [...this.gruposOriginal];
      this.totalRecords = this.gruposOriginal.length;
    }
  }

  private filtrarLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.grupos = this.gruposOriginal.filter(grupo => {
      if (this.isColumnVisible('codigoGrupo') && grupo.codigoGrupo != null) {
        if (String(grupo.codigoGrupo).includes(termLower)) return true;
      }
      if (this.isColumnVisible('nome') && grupo.nome) {
        if (grupo.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('criadoEm') && grupo.criadoEm) {
        const dataFormatada = new Date(grupo.criadoEm).toLocaleDateString('pt-BR');
        if (dataFormatada.toLowerCase().includes(termLower)) return true;
      }
      return false;
    });

    this.totalRecords = this.grupos.length;
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  submit() {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    if (this.form.nome === '' || this.form.nome === null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('grupo.texts.erro'),
        detail: this.translate.instant('grupo.texts.nome_obrigatorio'),
      });
      return;
    }

    const body: GrupoDTO = {
      nome: this.form.nome,
      status: this.form.status,
      ...(this.imageBase64 ? { imagem: this.imageBase64 } : {}),
    };

    this.submitting = true;
    this.grupoService.post('/grupo', body).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('grupo.texts.sucesso'),
          detail: this.translate.instant('grupo.texts.grupo_adicionado_sucesso'),
        });
        this.limparForm();
        this.findAtividade();
        this.activeTabIndex = 1;
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  salvarEdicaoDeGrupo() {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    if (this.form.nome === '' || this.form.nome === null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('grupo.texts.erro'),
        detail: this.translate.instant('grupo.texts.nome_obrigatorio'),
      });
      return;
    }

    const body: GrupoDTO = {
      nome: this.form.nome,
      status: this.form.status,
      ...(this.imageBase64 ? { imagem: this.imageBase64 } : this.imagemRemovida ? { imagem: '' } : {}),
    };

    this.submitting = true;
    this.grupoService.put('/grupo/' + this.form.id, body).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('grupo.texts.sucesso'),
          detail: this.translate.instant('grupo.texts.grupo_editado_sucesso'),
        });
        this.limparForm();
        this.findAtividade();
        this.activeTabIndex = 1;
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  deletarGrupo(id: string) {
    // Excluir sai do escopo add/edit sob contexto (backend não libera DELETE
    // /grupo no contexto). Bloqueio defensivo no modo embutido.
    if (this.embutido) return;
    this.deleteGrupoId = id;
    this.deleteDialogVisible = true;
  }

  onDeleteDialogShow() {
    setTimeout(() => {
      this.deleteConfirmBtnRef?.nativeElement?.focus();
    }, 50);
  }

  confirmarExclusao() {
    if (!this.deleteGrupoId) return;

    this.grupoService.delete(`/grupo/${this.deleteGrupoId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('grupo.texts.sucesso'),
          detail: this.translate.instant('grupo.texts.grupo_excluido_sucesso'),
        });
        this.deleteDialogVisible = false;
        this.deleteGrupoId = null;
        this.findAtividade();
      },
      error: () => {
        this.deleteDialogVisible = false;
        this.deleteGrupoId = null;
      },
    });
  }

  // =========================================================================
  // Compartilhar URL da categoria no Mini Shop
  // =========================================================================

  /**
   * Regra de habilitacao da acao de compartilhar: a categoria precisa estar
   * ATIVA (categorias inativas nao aparecem no Mini Shop publico, logo o link
   * seria inutil) E a loja precisa ter o Mini Shop configurado (slug presente).
   */
  podeCompartilharCategoria(grupo: GrupoDTO): boolean {
    return grupo?.status === true && !!this.slugLojaMiniShop;
  }

  /**
   * Monta a URL publica da categoria no Mini Shop:
   *   {origin}/menu/{slugLoja}?categoria={slugCategoria}
   *
   * - Base: window.location.origin (a propria web serve a rota publica /menu/:slug).
   * - slugLoja: urlDoMiniShopping da empresa logada (carregado da sessao).
   * - slugCategoria: slug do nome do grupo (a "categoria" no Mini Shop).
   *
   * Retorna string vazia quando faltar peca essencial (slug da loja ou slug da
   * categoria), evitando compartilhar uma URL invalida.
   */
  private montarUrlCategoria(grupo: GrupoDTO): string {
    const slugLoja = this.slugLojaMiniShop;
    const slugCategoria = slugificarCategoria(grupo?.nome);
    if (!slugLoja || !slugCategoria) {
      return '';
    }

    const base = `${window.location.origin}/menu/${encodeURIComponent(slugLoja)}`;
    return `${base}?categoria=${encodeURIComponent(slugCategoria)}`;
  }

  /**
   * Monta a URL publica da categoria no Mini Shop e copia para a area de
   * transferencia, exibindo um toast de confirmacao. Usa a Web Share API
   * quando disponivel (mobile) com fallback para copia (desktop).
   */
  compartilharCategoria(grupo: GrupoDTO): void {
    // Guarda defensivo: respeita a mesma regra do botao desabilitado.
    if (!this.podeCompartilharCategoria(grupo)) {
      return;
    }

    const url = this.montarUrlCategoria(grupo);
    if (!url) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.tooltips.compartilhar_indisponivel,
      });
      return;
    }

    if (navigator.share) {
      navigator
        .share({ title: grupo.nome, url })
        .catch((err) => {
          // Cancelamento do usuario (AbortError) nao deve exibir erro nem fallback.
          if (err?.name === 'AbortError') {
            return;
          }
          this.copiarUrlParaClipboard(url);
        });
      return;
    }

    this.copiarUrlParaClipboard(url);
  }

  /**
   * Copia a URL para a area de transferencia e exibe um toast de confirmacao.
   * Prioriza navigator.clipboard; se indisponivel, usa fallback com textarea
   * temporaria + execCommand. Mesmo padrao usado na listagem de produtos.
   */
  private copiarUrlParaClipboard(url: string): void {
    const notificarSucesso = () => {
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('cardapio_digital.link_copiado'),
        life: 2000,
      });
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(notificarSucesso)
        .catch(() => this.copiarUrlFallback(url, notificarSucesso));
      return;
    }

    this.copiarUrlFallback(url, notificarSucesso);
  }

  /** Fallback final de copia via textarea temporaria + execCommand. */
  private copiarUrlFallback(url: string, onSucesso: () => void): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      onSucesso();
    } catch {
      // Falha silenciosa — nao quebra a tela.
    }
  }

  createEmptyForm(): GrupoFormDTO {
    return {
      nome: '',
      status: true,
    };
  }

  limparForm() {
    this.form = this.createEmptyForm();
    this.imagemRemovida = false;
    this.fileName = null;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    if (this.fileUploaderRef) {
      this.fileUploaderRef.clear();
    }
    this.isFormSubmitted = false;

    // Voltar titulo para modo criacao
    this.texts.titulo = this.translate.instant('grupo.texts.titulo_criar');
    this.texts.subtitulo = this.translate.instant('grupo.texts.subtitulo_criar');
  }

  alternarStatus(grupo: GrupoDTO) {
    if (!grupo.id) return;

    this.grupoService.ativarOuInativar(grupo.id, grupo.status).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('grupo.texts.sucesso'),
          detail: grupo.status
            ? this.translate.instant('grupo.texts.grupo_ativado_sucesso')
            : this.translate.instant('grupo.texts.grupo_inativado_sucesso'),
        });
      },
      error: () => {
        // Reverter o status em caso de erro
        grupo.status = !grupo.status;
      },
    });
  }

  editarGrupo(event: GrupoDTO) {
    this.form = { ...event };
    this.imagemRemovida = false;

    // Atualizar titulo para modo edicao
    this.texts.titulo = this.translate.instant('grupo.texts.titulo_editar');
    this.texts.subtitulo = this.translate.instant('grupo.texts.subtitulo_editar');

    // Carrega preview da imagem existente
    if (event.imagem) {
      this.imagePreviewUrl = this.getImagemUrl(String(event.imagem));
      this.fileName = String(event.imagem);
    } else {
      this.imagePreviewUrl = null;
      this.fileName = null;
    }
    this.imageBase64 = null;
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getImagemUrl(imagem: string): string {
    if (!imagem) {
      return '';
    }
    // Se já for uma URL absoluta ou base64, usa diretamente
    if (imagem.startsWith('http') || imagem.startsWith('data:image')) {
      return imagem;
    }
    return `${this.env.imgHost}/${this.idEmpresa}/grupo/${imagem}`;
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
    const placeholder = (event.target as HTMLElement).nextElementSibling;
    if (placeholder) {
      (placeholder as HTMLElement).style.display = 'inline';
    }
  }

  abrirImagemAmpliada(grupo: GrupoDTO): void {
    if (grupo.imagem) {
      this.imagemAmpliadaSrc = this.getImagemUrl(String(grupo.imagem));
      this.imagemAmpliadaNome = grupo.nome || '';
      this.imagemAmpliadaVisible = true;
    }
  }

  novoGrupo(): void {
    this.limparForm();
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  // Atalhos de teclado
  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent): void {
    // Atalhos do dialog de exclusao
    if (this.deleteDialogVisible) {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.confirmarExclusao();
        return;
      }
      if (ev.key === 'Escape') {
        ev.preventDefault();
        this.deleteDialogVisible = false;
        return;
      }
      return;
    }

    // F2: focar no campo de busca
    if (ev.key === 'F2') {
      ev.preventDefault();
      setTimeout(() => {
        this.searchInputRef?.nativeElement?.focus();
      }, 100);
      return;
    }

    // Ctrl+Enter: adicionar ou salvar grupo
    if (ev.ctrlKey && ev.key === 'Enter') {
      ev.preventDefault();
      this.form.id ? this.salvarEdicaoDeGrupo() : this.submit();
      return;
    }
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }
}
